import { Util } from '@/utils/utils';
import { Constants } from '@/constants/constants';
import { Remote } from '@/api/remote';
import { ApiConstant } from '@/constants/api_constants';
import { useNavigate, useSearchParams } from 'react-router';
import { NAV_PATH } from '@/router/constants';

interface NoticeItem {
	anncmtTitl: string;
	viewDt?: string;
	regDt: string;
	anncmtId: string;
	newYn: string;
}

type ApiResponse = {
	code: string;
	message: string;
	content: {
		totalCnt: number;
		anncmtList: NoticeItem[];
	};
};

export const useNotice = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const NOTICE_PAGE_SIZE = 5;
	let noticeCurrentPage = searchParams.get('page') ? parseInt(searchParams.get('page') as string, 10) : 1;
	let noticeTotalCnt = 1;
	let noticeTotalPage = 1;
	const noticePagenationLen = 10;
	let noticeCurrentPageGroup = 1;
	let noticeTotalPageGroup = 1;

	const noticeInit = (): void => {
		noticeGetParams();
	};

	const noticeGetParams = (): void => {
		const params = Util.getUrlParameter();
		let page = params.page;
		if (Util.isNullOrEmpty(page)) {
			page = '1';
		}
		noticeCurrentPage = parseInt(page, 10);
		noticeGetData(noticeCurrentPage);
	};

	const noticeSetListEle = (list: NoticeItem[]): void => {
		if (list.length === 0) {
			document.querySelector('.board-pagination')?.classList.add('hidden');
			noticeShowContents();
			return;
		}
		const boardListCon = document.querySelector('.notice .board-list');
		const template = boardListCon?.getAttribute('data-template');
		let ele = '';
		list.forEach(() => (ele += template));
		if (boardListCon) boardListCon.innerHTML = ele;
		noticeSetList(list);
	};

	const noticeSetList = (list: NoticeItem[]): void => {
		const listEle = document.querySelectorAll<HTMLLIElement>('.notice .board-list li');

		list.forEach((item, i) => {
			const num = noticeTotalCnt - i - (noticeCurrentPage - 1) * NOTICE_PAGE_SIZE;
			const numElement = listEle[i].querySelector<HTMLElement>('.num');
			if (numElement) {
				numElement.innerText = num.toString();
			}

			// .title 요소에 안전하게 접근
			const titleElement = listEle[i].querySelector<HTMLElement>('.title');
			if (titleElement) {
				titleElement.innerText = item.anncmtTitl;
			}

			const regDt = item.viewDt?.split(Constants.space)[0] || item.regDt.split(Constants.space)[0];
			const strDt = regDt.split('-').join('');
			const date = Util.convertDate(strDt, '.');

			const dateElement = listEle[i].querySelector<HTMLElement>('.date');
			if (dateElement) {
				dateElement.innerText = date;
			}
			listEle[i].dataset.anncmtId = item.anncmtId;

			if (item.newYn === Constants.yes) {
				listEle[i].classList.add('new');
			}
			listEle[i].addEventListener('click', noticeClickList);
		});
		noticeSetPagenation();
		noticeShowContents();
	};

	/**
	 * 게시글 선택
	 * @param e
	 */
	const noticeClickList = (e: Event): void => {
		const target = e.currentTarget as HTMLElement;
		const anncmtId = target.dataset.anncmtId;
		const params = Util.getUrlParameter();
		const page = Util.isNullOrEmpty(params.page) ? '1' : params.page;

		// location.href = `/views/notice-detail.html?page=${page}&anncmtId=${anncmtId}`;
		navigate(`${NAV_PATH.CUSTOMER_SUPPORT.NOTICE_DETAIL}?page=${noticeCurrentPage}&anncmtId=${anncmtId}`);
	};

	/**
	 * 공지사항 데이터 조회
	 * @param page
	 */
	const noticeGetData = async (page: number): Promise<void> => {
		const jsonData = {
			page: page,
			size: NOTICE_PAGE_SIZE
		};

		const response: ApiResponse = await Remote.Api.get(ApiConstant.mgmtAnncmt.list, jsonData);

		if (response.code !== Constants.apiSuccess) {
			alert(response.message);
			return;
		}

		noticeTotalCnt = response.content.totalCnt;
		noticeTotalPage = Math.ceil(response.content.totalCnt / NOTICE_PAGE_SIZE);

		noticeSetListEle(response.content.anncmtList);
	};

	/**
	 * 페이징 버튼 설정
	 */
	const noticeSetPagenation = (): void => {
		const len = noticePagenationLen;
		const targetBtn = document.querySelectorAll<HTMLElement>('.pagination-btn');

		const currentPage = noticeCurrentPage;
		const totalPage = noticeTotalPage;
		noticeCurrentPageGroup = Math.ceil(currentPage / noticePagenationLen);
		noticeTotalPageGroup = Math.ceil(totalPage / noticePagenationLen);

		const start = (noticeCurrentPageGroup - 1) * noticePagenationLen;
		let end = noticeCurrentPageGroup * noticePagenationLen;
		const lastPagenationNum = noticePagenationLen * noticeTotalPageGroup - noticeTotalPage;

		if (noticeCurrentPageGroup === noticeTotalPageGroup) {
			end = totalPage;
		}

		for (let i = 0; i < len; i++) {
			const page = start + i + 1;
			targetBtn[i + 1].innerText = page.toString();

			// 기본을 비활성화로 설정
			targetBtn[i + 1].classList.remove('active');
			targetBtn[i + 1].classList.remove('hidden');

			// 현재 페이지 활성화
			if (currentPage - (noticeCurrentPageGroup - 1) * noticePagenationLen === i + 1) {
				targetBtn[i + 1].classList.add('active');
			}

			// 마지막 페이지 그룹
			if (noticeCurrentPageGroup === noticeTotalPageGroup && i >= len - lastPagenationNum) {
				targetBtn[i + 1].classList.add('hidden');
			}

			targetBtn[i + 1].addEventListener('click', noticeClickPagenation);
		}

		document.querySelector('.pagination-btn.btn-prev')?.addEventListener('click', noticeClickPagePrev);
		document.querySelector('.pagination-btn.btn-next')?.addEventListener('click', noticeClickPageNext);
		noticeCheckBtnPrevNext(noticeCurrentPageGroup, noticeTotalPageGroup);
	};

	/**
	 * 이전 페이지
	 */
	const noticeClickPagePrev = (): void => {
		let pageGroup = noticeCurrentPageGroup - 1;
		if (pageGroup < 1) {
			pageGroup = 1;
		}
		noticeCurrentPageGroup = pageGroup;
		const page = noticeGetCurrentPage(noticeCurrentPageGroup, noticeTotalPageGroup, noticeTotalPage, false);
		noticeChangePage(page);
	};

	/**
	 * 다음 페이지
	 */
	const noticeClickPageNext = (): void => {
		let pageGroup = noticeCurrentPageGroup + 1;
		if (pageGroup > noticeTotalPageGroup) {
			pageGroup = noticeTotalPageGroup;
		}
		noticeCurrentPageGroup = pageGroup;
		const page = noticeGetCurrentPage(noticeCurrentPageGroup, noticeTotalPageGroup, noticeTotalPage, true);
		noticeChangePage(page);
	};

	/**
	 * 페이지네이션 클릭
	 * @param e
	 */
	const noticeClickPagenation = (e: Event): void => {
		const target = e.currentTarget as HTMLElement;
		const page = parseInt(target.innerText, 10);

		noticeCurrentPage = page;
		noticeChangePage(page);
	};

	/**
	 * 페이지 변경
	 * @param page
	 */
	const noticeChangePage = (page: number): void => {
		// location.href = `/views/notice.html?page=${page}`;
		noticeGetData(Number(page));
	};

	/**
	 * 이전, 다음 버튼 활성화 여부
	 * @param currentPageGroup
	 * @param totalPageGroup
	 */
	const noticeCheckBtnPrevNext = (currentPageGroup: number, totalPageGroup: number): void => {
		const targetPrev = document.querySelector<HTMLButtonElement>('.pagination-btn.btn-prev');
		const targetNext = document.querySelector<HTMLButtonElement>('.pagination-btn.btn-next');

		if (currentPageGroup === 1) {
			noticeDisabledPagePrevNext(targetPrev);
		} else {
			noticeEnabledPagePrevNext(targetPrev);
		}

		if (currentPageGroup === totalPageGroup) {
			noticeDisabledPagePrevNext(targetNext);
		} else {
			noticeEnabledPagePrevNext(targetNext);
		}
	};

	/**
	 * 버튼 활성화
	 * @param target
	 */
	const noticeEnabledPagePrevNext = (target: HTMLButtonElement | null): void => {
		target?.removeAttribute('disabled');
	};

	/**
	 * 버튼 비활성화
	 * @param target
	 */
	const noticeDisabledPagePrevNext = (target: HTMLButtonElement | null): void => {
		target?.setAttribute('disabled', '');
	};

	/**
	 * 현재 페이지 번호
	 * @param currentPageGroup
	 * @param totalPageGroup
	 * @param totalPage
	 * @param isNext
	 */
	const noticeGetCurrentPage = (
		currentPageGroup: number,
		totalPageGroup: number,
		totalPage: number,
		isNext: boolean
	): number => {
		const len = noticePagenationLen;
		const start = (currentPageGroup - 1) * noticePagenationLen;
		let end = currentPageGroup * noticePagenationLen;

		if (currentPageGroup === totalPageGroup) {
			end = totalPage;
		}

		return isNext ? start + 1 : start + 1;
	};

	/**
	 * 컨텐츠 노출
	 */
	const noticeShowContents = (): void => {
		document.querySelector('body')?.classList.remove('is-scroll-y');
		document.querySelector('.notice')?.classList.remove('hidden');
		document.querySelector('.footer')?.classList.remove('hidden');
	};

	(() => {
		noticeInit();
	})();
};
